import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Truck, PlusCircle, Users, Contact, Building2,
  FileText, ClipboardList, PackageCheck, MessagesSquare, BarChart3,
  UserCog, Settings, ScrollText, LogOut, Upload, AlertTriangle, MessageSquare,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/dispatches", label: "Dispatches", icon: Truck, testid: "nav-dispatches" },
  { to: "/dispatches/new", label: "New Dispatch", icon: PlusCircle, testid: "nav-new-dispatch", roles: ["super_admin", "operations", "sales"] },
  { to: "/overdue", label: "Delivery Alerts", icon: AlertTriangle, testid: "nav-overdue" },
  { to: "/pod", label: "POD Center", icon: PackageCheck, testid: "nav-pod", roles: ["super_admin", "operations", "sales", "accounts"] },
  { to: "/queries", label: "Queries", icon: MessageSquare, testid: "nav-queries" },
  { to: "/import", label: "Excel Import", icon: Upload, testid: "nav-import", roles: ["super_admin", "operations", "sales", "accounts"] },
  { to: "/clients", label: "Clients", icon: Building2, testid: "nav-clients", roles: ["super_admin", "operations", "sales", "accounts"] },
  { to: "/transporters", label: "Transporters", icon: Truck, testid: "nav-transporters", roles: ["super_admin", "operations", "sales", "accounts"] },
  { to: "/communications", label: "Communications", icon: MessagesSquare, testid: "nav-communications", roles: ["super_admin", "operations", "sales", "accounts"] },
  { to: "/users", label: "Users", icon: UserCog, testid: "nav-users", roles: ["super_admin"] },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings", roles: ["super_admin"] },
  { to: "/audit", label: "Audit Logs", icon: ScrollText, testid: "nav-audit", roles: ["super_admin"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const roleAllowed = (item) => !item.roles || item.roles.includes(user?.role);
  const items = NAV.filter(roleAllowed);

  return (
    <div className="min-h-screen flex bg-[var(--app-bg)]">
      {/* Sidebar */}
      <aside className="w-64 bg-[var(--navy-deep)] text-slate-200 flex flex-col fixed inset-y-0 left-0 z-30">
        <div className="px-5 py-5 border-b border-slate-800">
          <div className="font-heading text-white text-[13px] font-extrabold tracking-tight leading-tight">SRAD SERVICES PRIVATE LIMITED</div>
          <div className="font-heading text-amber-400 text-base font-bold tracking-tight mt-0.5">Dispatch Hub</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1.5">Invoice • LR • Logistics</div>
        </div>
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testid}
              className={({ isActive }) =>
                `sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-md mb-1 text-sm ${
                  isActive ? "active text-amber-300" : "text-slate-400 hover:text-white hover:bg-slate-900"
                }`
              }
            >
              <item.icon size={17} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-800 p-4">
          <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">Signed in</div>
          <div className="text-sm text-white truncate">{user?.name}</div>
          <div className="text-xs text-slate-400">{user?.role?.replace("_", " ")}</div>
          <button
            data-testid="btn-logout"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="mt-3 w-full flex items-center justify-center gap-2 text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 py-2 rounded-md transition-colors"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64">
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 h-16 flex items-center px-8 justify-between">
          <div>
            <div className="font-heading text-lg font-bold text-slate-900">SRAD Dispatch Hub</div>
            <div className="text-xs text-slate-500">SRAD SERVICES PRIVATE LIMITED · ProcurMart</div>
          </div>
          <div className="text-sm text-slate-600">{user?.email}</div>
        </header>
        <main className="p-8 max-w-[1600px] mx-auto w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
